import { useState } from "react";
import { BlobServiceClient, ContainerClient, generateBlobSASQueryParameters, BlobSASPermissions, StorageSharedKeyCredential } from "@azure/storage-blob";

function UploadFileCard({ file, setFile, acceptedFileTypes = "pdf", displayText }) {
  // Define accept attribute and display text based on file types
  const getAcceptAttribute = (fileTypes) => {
    switch (fileTypes) {
      case "pdf":
        return ".pdf,application/pdf";
      case "image":
        return "image/*";
      case "both":
        return ".pdf,application/pdf,image/*";
      default:
        return ".pdf,application/pdf";
    }
  };

  const getDisplayText = (fileTypes) => {
    if (displayText) return displayText; // Use custom displayText if provided
    
    switch (fileTypes) {
      case "pdf":
        return "PDF files only";
      case "image":
        return "PNG, JPG files only";
      case "both":
        return "PDF, PNG, JPG files";
      default:
        return "PDF files only";
    }
  };

  return (
    <div>
      <div className="mt-1 flex justify-center px-4 pt-3 pb-4 border-2 border-gray-300 border-dashed rounded-md">
        <div className="space-y-1 text-center">
          <svg
            className="mx-auto h-8 w-8 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="flex text-sm text-gray-600">
            <label className="relative cursor-pointer bg-white rounded-md font-medium text-yellow-800 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
              <span>Upload a file</span>
              <input
                name="file-upload"
                type="file"
                accept={getAcceptAttribute(acceptedFileTypes)}
                className="sr-only"
                onChange={(event) => {
                  setFile(event.target.files[0] || null);
                }}
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-500">{getDisplayText(acceptedFileTypes)}</p>
        </div>
      </div>
      {file && (
        <div className="sm:col-span-6 mt-2">
          <label
            htmlFor="uploaded-files"
            className="block text-sm font-medium text-yellow-800"
          >
            Uploaded Files
          </label>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
            <span className="text-yellow-800 font-medium break-all flex-1 text-sm text-left">
              {file.name}
            </span>
            <span
              className="text-yellow-800 font-medium hover:text-red-700 cursor-pointer whitespace-nowrap flex-shrink-0 text-sm text-left"
              onClick={() => setFile(null)}
            >
              remove
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function useUploadFile({ container, acceptedFileTypes = "pdf", displayText }) {
  const [file, setFile] = useState(null);

  // Get environment-specific Azure configuration
  const getAzureConfig = () => {
    const environment = process.env.REACT_APP_ENV;
    
    let azureBlobSasUrl;
    let azureUrl;

    if (environment === "production") {
      azureBlobSasUrl = process.env.REACT_APP_AZURE_BLOB_SAS_PRODUCTION_URL;
      azureUrl = process.env.REACT_APP_AZURE_PRODUCTION_URL;
    } else if (environment === "staging") {
      azureBlobSasUrl = process.env.REACT_APP_AZURE_BLOB_SAS_STAGING_URL;
      azureUrl = process.env.REACT_APP_AZURE_STAGING_URL;
    }

    return { azureBlobSasUrl, azureUrl };
  };

  // Generate blob URL with SAS token for read access
  const generateBlobUrlWithSAS = (blobPath) => {
    try {
      const { azureBlobSasUrl, azureUrl } = getAzureConfig();
      
      if (!azureBlobSasUrl || !azureUrl) {
        throw new Error("Azure configuration not available for current environment");
      }
      
      // Extract SAS token from the existing SAS URL
      const sasUrl = new URL(azureBlobSasUrl);
      const sasToken = sasUrl.search;
      
      // Construct blob URL with SAS token
      const blobUrlWithSAS = `${azureUrl}/${container}/${blobPath}${sasToken}`;
      
      console.log('Generated blob URL with SAS:', blobUrlWithSAS);
      return blobUrlWithSAS;
    } catch (error) {
      console.error('Error generating blob URL with SAS:', error);
      throw error;
    }
  };

  async function uploadFile(blobPath) {
    if (!file) throw new Error("No file selected");
    if (!blobPath) throw new Error("blobPath is required");

    try {
      console.log('Starting upload to blob path:', blobPath);
      
      const { azureBlobSasUrl } = getAzureConfig();
      
      if (!azureBlobSasUrl) {
        throw new Error("Azure SAS URL not configured for current environment");
      }

      // Use BlobServiceClient for root-level SAS URL
      const blobServiceClient = new BlobServiceClient(azureBlobSasUrl);
      const containerClient = blobServiceClient.getContainerClient(container);
      const blockBlobClient = containerClient.getBlockBlobClient(blobPath);

      // Convert file to ArrayBuffer and upload
      const arrayBuffer = await file.arrayBuffer();
      
      // Set appropriate content type based on file type
      const contentType = file.type || (file.name.toLowerCase().includes('.pdf') ? "application/pdf" : "image/jpeg");
      
      await blockBlobClient.uploadData(arrayBuffer, {
        blobHTTPHeaders: { blobContentType: contentType },
      });

      console.log(`Successfully uploaded to ${container}/${blobPath}`);
      
      // Return the blob URL with SAS token for database storage
      const blobUrlWithSAS = generateBlobUrlWithSAS(blobPath);
      console.log('Returning blob URL with SAS for database storage:', blobUrlWithSAS);
      
      return blobUrlWithSAS;
      
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.statusCode,
        request: error.request?.url
      });
      throw error;
    }
  }

  // Reset function to clear file state
  const resetFile = () => {
    setFile(null);
  };

  const fileCard = <UploadFileCard file={file} setFile={setFile} acceptedFileTypes={acceptedFileTypes} displayText={displayText} />;

  return [file, fileCard, uploadFile, resetFile];
}

export { useUploadFile };